/**
 * A run's steps grouped into the ETAPY a person can actually hold in their head
 * (Pavel 2026-08-05: "ty etapy propracovat, aby to nebylo takový nudný na to
 * koukat a čekat").
 *
 * A flat list of twenty e-mail steps is a wall of text: it says nothing about
 * where the build IS, and every step looks equally important. The catalog
 * already carries the answer - `stepClass` names what a step is FOR (prep,
 * generate, critique, qa, assembly, delivery) - so the timeline groups by it.
 *
 * Two rules keep the grouping honest:
 *
 * - Stages are CONTIGUOUS runs of one class, never a global bucket. The sales
 *   page runs qa at step 9 and again at 12-13; merging both into one "Kontrola"
 *   would put a stage before and after itself. Two separate Kontrola blocks is
 *   what actually happens, so that is what is shown.
 * - A duration is printed only when every step behind it was measured. A stage
 *   whose events start mid-run would otherwise report the sum of the half it
 *   saw as if it were the whole, and quietly under-report the wait.
 *
 * The step ORDER and the "krok X z Y" counter still come from `dfyRunProgress`.
 * This module never counts its own way.
 */
import {
  dfyRunProgress,
  dfyStepClass,
  type DfyRunProgress,
  type DfyRunStep,
  type DfyStepClass
} from "./dfy-catalog";
import type { DfyStepObservationDto, DfyStepTypicalDto } from "./dto";

/**
 * `skipped` is its own state on purpose. A build legitimately passes over
 * optional steps (a client sent no photos, a campaign shape has no downsell),
 * and those steps sit BEHIND the cursor without ever having run. Calling them
 * done gave them a check mark they had not earned, and made the timeline show
 * more finished steps than the "krok X z Y" header counted.
 */
export type DfyStageState = "done" | "current" | "upcoming" | "skipped";

export interface DfyRunStageStep {
  key: string;
  /** 1-based position in the whole run, so a step can name itself the same way
   * the header does ("krok 7 z 15"). */
  index: number;
  state: DfyStageState;
  /** What THIS run measured for this step; null until the server saw it end. */
  durationMs: number | null;
  /** What the step usually costs. Never summed into a completion promise. */
  typicalMs: number | null;
}

export interface DfyRunStage {
  /** Stable across polls: class plus the position it starts at. */
  id: string;
  stepClass: DfyStepClass;
  state: DfyStageState;
  steps: DfyRunStageStep[];
  doneCount: number;
  /** Measured total, only when every step in the stage was measured. */
  durationMs: number | null;
  /** Historical total, only when every step in the stage has a median. */
  typicalMs: number | null;
}

export interface DfyRunTimeline {
  progress: DfyRunProgress;
  stages: DfyRunStage[];
}

export interface DfyRunTimelineInput {
  gateKey: string;
  steps: readonly DfyRunStep[];
  stepManifest?: readonly string[] | null;
  observed?: readonly DfyStepObservationDto[];
  typical?: readonly DfyStepTypicalDto[];
}

/** Locale key for a stage heading. */
export function dfyStageLabelKey(stepClass: DfyStepClass): string {
  return `dfyEta.stages.${stepClass}`;
}

/**
 * The class a step belongs to on the timeline.
 *
 * The runner may report a campaign phase the catalog has not learned yet
 * ("faze-anything"); those are generation work by construction. Anything else
 * unknown inherits the step before it, so a new step lands in the stage it
 * actually happened in rather than opening a stage of its own.
 */
function classOf(step: string, previous: DfyStepClass | null): DfyStepClass {
  const known = dfyStepClass(step);
  if (known) return known;
  if (step.startsWith("faze-")) return "generate";
  return previous ?? "generate";
}

export function dfyRunTimeline(input: DfyRunTimelineInput): DfyRunTimeline {
  const progress = dfyRunProgress(input.gateKey, input.steps, input.stepManifest ?? null);
  const done = new Set(input.steps.filter((entry) => entry.ok).map((entry) => entry.step));
  const durations = new Map<string, number>();
  for (const entry of input.observed ?? []) {
    if (Number.isFinite(entry.durationMs) && entry.durationMs >= 0) durations.set(entry.stepKey, entry.durationMs);
  }
  const typicals = new Map<string, number>();
  for (const entry of input.typical ?? []) {
    if (Number.isFinite(entry.medianMs) && entry.medianMs >= 0) typicals.set(entry.stepKey, entry.medianMs);
  }

  const currentIndex = progress.currentStepKey ? progress.stepKeys.indexOf(progress.currentStepKey) : -1;
  const stages: DfyRunStage[] = [];
  let previousClass: DfyStepClass | null = null;

  progress.stepKeys.forEach((key, index) => {
    const stepClass = classOf(key, previousClass);
    previousClass = stepClass;
    const behindCursor = currentIndex === -1 || index < currentIndex;
    const state: DfyStageState = index === currentIndex
      ? "current"
      : done.has(key)
        ? "done"
        : behindCursor ? "skipped" : "upcoming";
    const step: DfyRunStageStep = {
      key,
      index: index + 1,
      state,
      durationMs: durations.has(key) ? durations.get(key)! : null,
      typicalMs: typicals.has(key) ? typicals.get(key)! : null
    };
    const open = stages[stages.length - 1];
    if (open && open.stepClass === stepClass) {
      open.steps.push(step);
      return;
    }
    stages.push({
      id: `${stepClass}-${index + 1}`,
      stepClass,
      state: "upcoming",
      steps: [step],
      doneCount: 0,
      durationMs: null,
      typicalMs: null
    });
  });

  for (const stage of stages) {
    stage.doneCount = stage.steps.filter((step) => step.state === "done").length;
    stage.state = stage.steps.some((step) => step.state === "current")
      ? "current"
      : stage.steps.every((step) => step.state === "skipped")
        ? "skipped"
        : stage.steps.every((step) => step.state === "done" || step.state === "skipped")
          ? "done"
          : "upcoming";
    // Only a fully measured stage may print a number: a partially observed one
    // would report the half it saw as the whole wait. Skipped steps cost
    // nothing observable and neither prove nor spoil the total.
    const measured = stage.steps.filter((step) => step.state === "done");
    stage.durationMs = measured.length > 0 && measured.every((step) => step.durationMs !== null)
      ? measured.reduce((total, step) => total + (step.durationMs ?? 0), 0)
      : null;
    stage.typicalMs = stage.steps.every((step) => step.typicalMs !== null)
      ? stage.steps.reduce((total, step) => total + (step.typicalMs ?? 0), 0)
      : null;
  }

  return { progress, stages };
}
